/**
 * Generated by orval 🍺
 * Do not edit manually.
 * RK Meeting API – meetings
 * Meetings visible to the caller (own, shared directly or via a group; admins see all). Invisible meetings answer 404, visible ones without the needed level 403. Updates use optimistic locking: the ETag is the meeting version, PATCH requires it in If-Match (412 on conflict).
 *
 * OpenAPI spec version: 1.0.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { apiFetch } from '../fetch';
export type MeetingStatus = typeof MeetingStatus[keyof typeof MeetingStatus];


export const MeetingStatus = {
  draft: 'draft',
  uploading: 'uploading',
  processing: 'processing',
  awaiting_review: 'awaiting_review',
  generating: 'generating',
  ready: 'ready',
  failed: 'failed',
} as const;

/**
 * The caller's access; admin = admin without own share; review = in the circulation of the protocol (protocol, decisions, action items and PDF only).
 */
export type AccessLevel = typeof AccessLevel[keyof typeof AccessLevel];


export const AccessLevel = {
  review: 'review',
  read: 'read',
  edit: 'edit',
  admin: 'admin',
  owner: 'owner',
} as const;

export interface MeetingCreate {
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  heldAt?: string;
  /** @maxLength 300 */
  location?: string;
  /**
     * ISO 639-1 language of the recording; default TRANSCRIPTION_LANGUAGE.
     * @pattern ^[a-z]{2,3}$
     */
  language?: string;
}

/**
 * Absent fields stay unchanged; heldAt null and an empty location clear the value.
 */
export interface MeetingUpdate {
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  /** @nullable */
  heldAt?: string | null;
  /** @maxLength 300 */
  location?: string;
  /** @pattern ^[a-z]{2,3}$ */
  language?: string;
}

/**
 * Approval state of the protocol; none = no protocol version yet.
 */
export type MeetingProtocolStatus = typeof MeetingProtocolStatus[keyof typeof MeetingProtocolStatus];


export const MeetingProtocolStatus = {
  none: 'none',
  draft: 'draft',
  in_review: 'in_review',
  approved: 'approved',
} as const;

export type MeetingSummarySource = typeof MeetingSummarySource[keyof typeof MeetingSummarySource];


export const MeetingSummarySource = {
  upload: 'upload',
  recording: 'recording',
} as const;

export interface MeetingSummary {
  id: string;
  title: string;
  heldAt?: string;
  location?: string;
  language: string;
  status: MeetingStatus;
  protocolStatus: MeetingProtocolStatus;
  source?: MeetingSummarySource;
  durationMs?: number;
  ownerId: string;
  ownerName: string;
  access: AccessLevel;
  createdAt: string;
  updatedAt: string;
}

export type Meeting = MeetingSummary & {
  /** Lock version; the ETag carries the same value. */
  version: number;
  recordingConsentConfirmedAt?: string;
};

export interface MeetingPage {
  items: MeetingSummary[];
  /** Absent on the last page. */
  nextCursor?: string;
}

/**
 * RFC 9457 problem details, sent as application/problem+json. The frontend maps `code` (or the type slug) to a German message.
 */
export interface Problem {
  /** Stable, English problem type URI. */
  type: string;
  title: string;
  status: number;
  /** English explanation of this occurrence. Never contains personal data. */
  detail?: string;
  /** Request path of this occurrence. */
  instance?: string;
  /** Request ID for correlating with server logs. */
  requestId?: string;
  /** Stable machine-readable code, e.g. protocol_version_conflict. */
  code?: string;
  /** Optional machine-readable details, e.g. validation errors. */
  details?: unknown;
}

/**
 * ETag of the version the change is based on.
 */
export type IfMatchParameter = string;

export type ListMeetingsParams = {
  /**
   * Full-text search in title and location (German stemming) plus title substring.
   * @maxLength 200
   */
  q?: string;
  scope?: ListMeetingsScope;
  status?: MeetingStatus;
  /** Only meetings whose protocol has this approval state (none = no protocol yet). */
  protocolStatus?: MeetingProtocolStatus;
  /** my_approval = protocols in review that the caller approves as chair (or as owner when no chair has an account); my_feedback = protocols in review whose circulation still waits for the caller's feedback. */
  awaiting?: ListMeetingsAwaiting;
  /** Sort order, always newest first: held_at (held at, else created at), updated_at or created_at. A cursor only continues the sort order it was issued for (otherwise 400 invalid_cursor). */
  sort?: ListMeetingsSort;
  /**
   * Opaque cursor from nextCursor of the previous page.
   * @maxLength 200
   */
  cursor?: string;
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
};

export type ListMeetingsScope = typeof ListMeetingsScope[keyof typeof ListMeetingsScope];


export const ListMeetingsScope = {
  all: 'all',
  mine: 'mine',
  shared: 'shared',
} as const;

export type ListMeetingsAwaiting = typeof ListMeetingsAwaiting[keyof typeof ListMeetingsAwaiting];


export const ListMeetingsAwaiting = {
  my_approval: 'my_approval',
  my_feedback: 'my_feedback',
} as const;

export type ListMeetingsSort = typeof ListMeetingsSort[keyof typeof ListMeetingsSort];


export const ListMeetingsSort = {
  held_at: 'held_at',
  updated_at: 'updated_at',
  created_at: 'created_at',
} as const;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type listMeetingsResponse200 = {
  data: MeetingPage
  status: 200
}

export type listMeetingsResponse400 = {
  data: Problem
  status: 400
}

export type listMeetingsResponse401 = {
  data: Problem
  status: 401
}

export type listMeetingsResponse403 = {
  data: Problem
  status: 403
}

export type listMeetingsResponseSuccess = (listMeetingsResponse200) & {
  headers: Headers;
};
export type listMeetingsResponseError = (listMeetingsResponse400 | listMeetingsResponse401 | listMeetingsResponse403) & {
  headers: Headers;
};

export type listMeetingsResponse = (listMeetingsResponseSuccess | listMeetingsResponseError)

export const getListMeetingsUrl = (params?: ListMeetingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/meetings?${stringifiedParams}` : `/api/meetings`
}

/**
 * @summary List visible meetings
 */
export const listMeetings = async (params?: ListMeetingsParams, options?: Parameters<typeof apiFetch>[1]): Promise<listMeetingsResponse> => {

  return apiFetch<listMeetingsResponse>(getListMeetingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMeetingsQueryKey = (params?: ListMeetingsParams,) => {
    return [
    `/api/meetings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMeetingsQueryOptions = <TData = Awaited<ReturnType<typeof listMeetings>>, TError = Problem>(params?: ListMeetingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMeetings>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMeetingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMeetings>>> = ({ signal }) => listMeetings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMeetings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMeetingsQueryResult = NonNullable<Awaited<ReturnType<typeof listMeetings>>>
export type ListMeetingsQueryError = Problem


export function useListMeetings<TData = Awaited<ReturnType<typeof listMeetings>>, TError = Problem>(
 params: undefined |  ListMeetingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMeetings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMeetings>>,
          TError,
          Awaited<ReturnType<typeof listMeetings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMeetings<TData = Awaited<ReturnType<typeof listMeetings>>, TError = Problem>(
 params?: ListMeetingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMeetings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMeetings>>,
          TError,
          Awaited<ReturnType<typeof listMeetings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMeetings<TData = Awaited<ReturnType<typeof listMeetings>>, TError = Problem>(
 params?: ListMeetingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMeetings>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List visible meetings
 */

export function useListMeetings<TData = Awaited<ReturnType<typeof listMeetings>>, TError = Problem>(
 params?: ListMeetingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMeetings>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMeetingsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type createMeetingResponse201 = {
  data: Meeting
  status: 201
}

export type createMeetingResponse400 = {
  data: Problem
  status: 400
}

export type createMeetingResponse401 = {
  data: Problem
  status: 401
}

export type createMeetingResponse403 = {
  data: Problem
  status: 403
}

export type createMeetingResponseSuccess = (createMeetingResponse201) & {
  headers: Headers;
};
export type createMeetingResponseError = (createMeetingResponse400 | createMeetingResponse401 | createMeetingResponse403) & {
  headers: Headers;
};

export type createMeetingResponse = (createMeetingResponseSuccess | createMeetingResponseError)

export const getCreateMeetingUrl = () => {




  return `/api/meetings`
}

/**
 * @summary Create a meeting
 */
export const createMeeting = async (meetingCreate: MeetingCreate, options?: Parameters<typeof apiFetch>[1]): Promise<createMeetingResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<createMeetingResponse>(getCreateMeetingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(meetingCreate)
  }
);}





export const getCreateMeetingMutationKey = () => ['createMeeting'] as const;

export const getCreateMeetingMutationOptions = <TError = Problem,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMeeting>>, TError,CreateMeetingMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMeeting>>, TError,CreateMeetingMutationVariables, TContext> => {

const mutationKey = getCreateMeetingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMeeting>>, CreateMeetingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMeeting(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMeetingMutationResult = NonNullable<Awaited<ReturnType<typeof createMeeting>>>
    export type CreateMeetingMutationBody = MeetingCreate
    export type CreateMeetingMutationError = Problem
    export type CreateMeetingMutationVariables = {data: MeetingCreate}

    /**
 * @summary Create a meeting
 */
export const useCreateMeeting = <TError = Problem,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMeeting>>, TError,CreateMeetingMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createMeeting>>,
        TError,
        CreateMeetingMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMeetingMutationOptions(options), queryClient);
    }

export type getMeetingResponse200 = {
  data: Meeting
  status: 200
}

export type getMeetingResponse401 = {
  data: Problem
  status: 401
}

export type getMeetingResponse403 = {
  data: Problem
  status: 403
}

export type getMeetingResponse404 = {
  data: Problem
  status: 404
}

export type getMeetingResponseSuccess = (getMeetingResponse200) & {
  headers: Headers;
};
export type getMeetingResponseError = (getMeetingResponse401 | getMeetingResponse403 | getMeetingResponse404) & {
  headers: Headers;
};

export type getMeetingResponse = (getMeetingResponseSuccess | getMeetingResponseError)

export const getGetMeetingUrl = (meetingId: string,) => {




  return `/api/meetings/${meetingId}`
}

/**
 * @summary One meeting with the caller's access level
 */
export const getMeeting = async (meetingId: string, options?: Parameters<typeof apiFetch>[1]): Promise<getMeetingResponse> => {

  return apiFetch<getMeetingResponse>(getGetMeetingUrl(meetingId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMeetingQueryKey = (meetingId: string,) => {
    return [
    `/api/meetings/${meetingId}`
    ] as const;
    }


export const getGetMeetingQueryOptions = <TData = Awaited<ReturnType<typeof getMeeting>>, TError = Problem>(meetingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMeeting>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMeetingQueryKey(meetingId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMeeting>>> = ({ signal }) => getMeeting(meetingId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: meetingId !== null && meetingId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMeeting>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMeetingQueryResult = NonNullable<Awaited<ReturnType<typeof getMeeting>>>
export type GetMeetingQueryError = Problem


export function useGetMeeting<TData = Awaited<ReturnType<typeof getMeeting>>, TError = Problem>(
 meetingId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMeeting>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMeeting>>,
          TError,
          Awaited<ReturnType<typeof getMeeting>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMeeting<TData = Awaited<ReturnType<typeof getMeeting>>, TError = Problem>(
 meetingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMeeting>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMeeting>>,
          TError,
          Awaited<ReturnType<typeof getMeeting>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMeeting<TData = Awaited<ReturnType<typeof getMeeting>>, TError = Problem>(
 meetingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMeeting>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One meeting with the caller's access level
 */

export function useGetMeeting<TData = Awaited<ReturnType<typeof getMeeting>>, TError = Problem>(
 meetingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMeeting>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMeetingQueryOptions(meetingId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export type updateMeetingResponse200 = {
  data: Meeting
  status: 200
}

export type updateMeetingResponse400 = {
  data: Problem
  status: 400
}

export type updateMeetingResponse401 = {
  data: Problem
  status: 401
}

export type updateMeetingResponse403 = {
  data: Problem
  status: 403
}

export type updateMeetingResponse404 = {
  data: Problem
  status: 404
}

export type updateMeetingResponse409 = {
  data: Problem
  status: 409
}

export type updateMeetingResponse412 = {
  data: Problem
  status: 412
}

export type updateMeetingResponseSuccess = (updateMeetingResponse200) & {
  headers: Headers;
};
export type updateMeetingResponseError = (updateMeetingResponse400 | updateMeetingResponse401 | updateMeetingResponse403 | updateMeetingResponse404 | updateMeetingResponse409 | updateMeetingResponse412) & {
  headers: Headers;
};

export type updateMeetingResponse = (updateMeetingResponseSuccess | updateMeetingResponseError)

export const getUpdateMeetingUrl = (meetingId: string,) => {




  return `/api/meetings/${meetingId}`
}

/**
 * Needs edit access. Absent fields stay unchanged; an empty location clears it. While the protocol is in review or approved, title, date and location cannot change (409 protocol_locked).
 * @summary Change title, date, location or language
 */
export const updateMeeting = async (meetingId: string,
    meetingUpdate: MeetingUpdate, options?: Parameters<typeof apiFetch>[1]): Promise<updateMeetingResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<updateMeetingResponse>(getUpdateMeetingUrl(meetingId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(meetingUpdate)
  }
);}





export const getUpdateMeetingMutationKey = () => ['updateMeeting'] as const;

export const getUpdateMeetingMutationOptions = <TError = Problem,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMeeting>>, TError,UpdateMeetingMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateMeeting>>, TError,UpdateMeetingMutationVariables, TContext> => {

const mutationKey = getUpdateMeetingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateMeeting>>, UpdateMeetingMutationVariables> = (props) => {
          const {meetingId,data} = props ?? {};

          return  updateMeeting(meetingId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateMeetingMutationResult = NonNullable<Awaited<ReturnType<typeof updateMeeting>>>
    export type UpdateMeetingMutationBody = MeetingUpdate
    export type UpdateMeetingMutationError = Problem
    export type UpdateMeetingMutationVariables = {meetingId: string;data: MeetingUpdate}

    /**
 * @summary Change title, date, location or language
 */
export const useUpdateMeeting = <TError = Problem,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMeeting>>, TError,UpdateMeetingMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateMeeting>>,
        TError,
        UpdateMeetingMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateMeetingMutationOptions(options), queryClient);
    }

export type deleteMeetingResponse204 = {
  data: void
  status: 204
}

export type deleteMeetingResponse401 = {
  data: Problem
  status: 401
}

export type deleteMeetingResponse403 = {
  data: Problem
  status: 403
}

export type deleteMeetingResponse404 = {
  data: Problem
  status: 404
}

export type deleteMeetingResponseSuccess = (deleteMeetingResponse204) & {
  headers: Headers;
};
export type deleteMeetingResponseError = (deleteMeetingResponse401 | deleteMeetingResponse403 | deleteMeetingResponse404) & {
  headers: Headers;
};

export type deleteMeetingResponse = (deleteMeetingResponseSuccess | deleteMeetingResponseError)

export const getDeleteMeetingUrl = (meetingId: string,) => {




  return `/api/meetings/${meetingId}`
}

/**
 * Owner or admin only. The meeting disappears immediately and is purged after MEETING_PURGE_AFTER.
 * @summary Delete a meeting (soft delete)
 */
export const deleteMeeting = async (meetingId: string, options?: Parameters<typeof apiFetch>[1]): Promise<deleteMeetingResponse> => {

  return apiFetch<deleteMeetingResponse>(getDeleteMeetingUrl(meetingId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteMeetingMutationKey = () => ['deleteMeeting'] as const;

export const getDeleteMeetingMutationOptions = <TError = Problem,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMeeting>>, TError,DeleteMeetingMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteMeeting>>, TError,DeleteMeetingMutationVariables, TContext> => {

const mutationKey = getDeleteMeetingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteMeeting>>, DeleteMeetingMutationVariables> = (props) => {
          const {meetingId} = props ?? {};

          return  deleteMeeting(meetingId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteMeetingMutationResult = NonNullable<Awaited<ReturnType<typeof deleteMeeting>>>

    export type DeleteMeetingMutationError = Problem
    export type DeleteMeetingMutationVariables = {meetingId: string}

    /**
 * @summary Delete a meeting (soft delete)
 */
export const useDeleteMeeting = <TError = Problem,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMeeting>>, TError,DeleteMeetingMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteMeeting>>,
        TError,
        DeleteMeetingMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteMeetingMutationOptions(options), queryClient);
    }
